import type {
  YachtCategory,
  YachtScorePreview,
  YachtViewerState,
  YachtViewerTurnState,
} from "@cloudnest/yacht-engine";
import { DiceTray } from "./DiceTray";
import { RollMeter } from "./RollMeter";

function getSelectedPreview(
  previews: readonly YachtScorePreview[],
  category: YachtCategory | null,
): YachtScorePreview | null {
  if (category === null) return null;
  return previews.find((preview) => preview.category === category) ?? null;
}

export function ActionDock({
  state,
  selectedCategory,
  readOnly,
  onRoll,
  onToggleHold,
  onConfirmScore,
  onClearSelectedCategory,
}: {
  readonly state: YachtViewerState;
  readonly selectedCategory: YachtCategory | null;
  readonly readOnly: boolean;
  readonly onRoll: (slot: YachtViewerTurnState) => void;
  readonly onToggleHold: (slot: YachtViewerTurnState, dieIndex: number) => void;
  readonly onConfirmScore: (
    slot: YachtViewerTurnState,
    category: YachtCategory,
  ) => void;
  readonly onClearSelectedCategory: () => void;
}) {
  const viewerSlot = state.viewerPlayerId
    ? state.turnSlotsByPlayerId[state.viewerPlayerId]
    : undefined;
  const activeSlot =
    state.seatingOrder
      .map((playerId) => state.turnSlotsByPlayerId[playerId])
      .find((slot) => slot?.status === "OPEN") ?? undefined;
  const displaySlot =
    state.turnMode === "SIMULTANEOUS"
      ? (viewerSlot ?? activeSlot)
      : viewerSlot?.status === "OPEN"
        ? viewerSlot
        : activeSlot;
  const displayPlayer = state.players.find(
    (player) => player.playerId === displaySlot?.playerId,
  );
  const isViewerActionSlot =
    displaySlot !== undefined && displaySlot.playerId === state.viewerPlayerId;
  const selectedPreview = getSelectedPreview(
    state.legalActions.scoreableCategories,
    selectedCategory,
  );
  const dice = displaySlot?.dice ?? [null, null, null, null, null];
  const held = displaySlot?.held ?? [false, false, false, false, false];
  const rollCount = displaySlot?.rollCount ?? 0;
  const canConfirm = Boolean(
    isViewerActionSlot && viewerSlot && selectedCategory && selectedPreview,
  );
  const hasHeldDice = held.some(Boolean);
  const isOpeningRoll = rollCount === 0 && dice.every((die) => die === null);
  const isFullReroll = rollCount > 0 && !hasHeldDice;
  const canRoll = Boolean(
    isViewerActionSlot &&
      viewerSlot &&
      state.legalActions.canRoll &&
      !readOnly &&
      !selectedCategory,
  );
  const canClearHeldDice = Boolean(
    isViewerActionSlot && !readOnly && hasHeldDice,
  );
  const canCancel = Boolean(selectedCategory || canClearHeldDice);
  const actionKind = selectedPreview ? "score" : canRoll ? "roll" : "none";
  const actionLabel =
    actionKind === "score"
      ? `점수 확정 +${selectedPreview?.score ?? 0}`
      : actionKind === "roll"
        ? isOpeningRoll
          ? "굴리기"
          : isFullReroll
            ? "전체 리롤"
            : "리롤"
        : "";
  const statusLabel =
    displaySlot === undefined
      ? "대기"
      : isViewerActionSlot
        ? "내 차례"
        : `${displayPlayer?.displayName ?? displaySlot.playerId} 차례`;

  return (
    <section
      className="yacht-action-dock"
      data-testid="yacht-action-dock"
      data-viewer-turn={isViewerActionSlot ? "true" : "false"}
    >
      <div className="yacht-action-context" data-testid="yacht-action-context">
        <strong>{statusLabel}</strong>
        <RollMeter rollCount={rollCount} />
      </div>
      <div className="yacht-dice-roll-row">
        <DiceTray
          dice={dice}
          held={held}
          holdableDice={
            isViewerActionSlot ? state.legalActions.holdableDice : []
          }
          readOnly={readOnly || !isViewerActionSlot || !viewerSlot}
          onToggleHold={(dieIndex) => {
            if (viewerSlot && isViewerActionSlot) {
              onToggleHold(viewerSlot, dieIndex);
            }
          }}
        />
      </div>
      <div className="yacht-action-buttons">
        <button
          className="yacht-button ghost"
          data-testid="yacht-cancel-action-button"
          disabled={!canCancel}
          onClick={() => {
            if (selectedCategory) {
              onClearSelectedCategory();
              return;
            }
            if (viewerSlot && canClearHeldDice) {
              held.forEach((isHeld, dieIndex) => {
                if (isHeld) onToggleHold(viewerSlot, dieIndex);
              });
            }
          }}
          type="button"
        >
          취소
        </button>
        <button
          className="yacht-button primary"
          data-action-kind={actionKind}
          data-roll-scope={
            actionKind === "roll"
              ? isOpeningRoll
                ? "opening"
                : isFullReroll
                  ? "all"
                  : "held"
              : "none"
          }
          data-testid="yacht-primary-action-button"
          disabled={
            readOnly ||
            !viewerSlot ||
            (actionKind === "score" ? !canConfirm : !canRoll)
          }
          onClick={() => {
            if (actionKind === "score" && viewerSlot && selectedCategory) {
              onConfirmScore(viewerSlot, selectedCategory);
              return;
            }
            if (actionKind === "roll" && viewerSlot) {
              onRoll(viewerSlot);
            }
          }}
          type="button"
        >
          {actionLabel}
        </button>
      </div>
    </section>
  );
}
